/** Platform-admin screens: cross-tenant users, the plan catalogue, companies, email, onboarding. */
const en = {
  needSuperAdmin: 'You need platform super-admin access to view this page.',
  users: {
    title: 'Users',
    subtitle:
      'Every user across every tenant. Enable or disable accounts and grant platform super-admin access. Disabling an account blocks login immediately.',
    updated: 'User updated',
    updateFailed: 'Update failed',
    columns: {
      user: 'User',
      companies: 'Companies',
      lastLogin: 'Last login',
      active: 'Active',
      superAdmin: 'Super-admin',
    },
    verified: 'Email verified',
    notVerified: 'Email not verified',
    selfDisable: "You can't disable your own account",
    disable: 'Disable account',
    enable: 'Enable account',
    allowlisted:
      'Super-admin via SUPER_ADMIN_EMAILS allowlist. Change it in the deploy config',
    toggleSuperAdmin: 'Toggle platform super-admin',
  },
  plans: {
    title: 'Plan Catalogue',
    subtitle:
      'Edit the global plan catalogue: limits, pricing and feature gates. Changes apply to new sessions immediately across every tenant.',
    columns: {
      plan: 'Plan',
      price: '$/mo',
      sessionsPerDay: 'Sessions / day',
      sessionsPerMonth: 'Sessions / month',
      projects: 'Projects',
      integrationTools: 'Integration tools',
      coverOps: 'CoverOps',
      retention: 'Retention (d)',
      seats: 'Seats',
      enabled: 'Enabled',
      integrations: 'Integrations',
      apm: 'APM',
      pii: 'PII mask',
      ingestPii: 'Ingest PII mask',
      sso: 'SSO',
      tester: 'Tester mode',
      testers: 'Testers',
    },
    blankInfinite: 'blank = ∞',
    zeroInfinite: '0 = ∞',
    coverOpsModes: { none: 'Not offered', addon: 'Add-on', included: 'Included' } as Record<string, string>,
  },
  companies: {
    title: 'Companies',
    subtitle:
      "Every tenant on the platform. Change a company's subscription plan here. It takes effect on their next sessions immediately.",
    columns: {
      company: 'Company',
      owner: 'Owner',
      members: 'Members',
      logins: 'Logins',
      sessions: 'Sessions',
      onboarding: 'Onboarding',
      status: 'Status',
      subscription: 'Subscription',
      renews: 'Renews',
      joined: 'Joined',
    },
    addons: {
      button: 'Add-ons',
      title: (name: string) => `Add-ons · ${name}`,
      note: 'Each add-on raises one limit on top of the package. Sessions are a count, sold in blocks of 1,000.',
      sessions: 'Extra sessions / month',
      projects: 'Extra projects',
      integrations: 'Extra integration tools',
      coverOps: 'CoverOps add-on',
      summary: (s: string, p: number, i: number, c: boolean) =>
        [s !== '0' && `+${s} sessions`, p > 0 && `+${p} projects`, i > 0 && `+${i} tools`, c && 'CoverOps']
          .filter(Boolean)
          .join(' · '),
      onFree: 'Assign a package first; Free has nothing to add to.',
      save: 'Save add-ons',
      saved: 'Add-ons saved',
    },
    actAs: 'Open the dashboard as this company (super-admin)',
    loginsHint: "Successful sign-ins by this company's members",
    memberColumns: { user: 'User', role: 'Role', status: 'Status', logins: 'Logins', lastLogin: 'Last login' },
    detailsFailed: "Could not load this company's onboarding details.",
  },
  demoRequests: {
    title: 'Demo requests',
    subtitle:
      'Leads from the landing-page form. The status column is the point: anything still at New is somebody nobody has answered.',
    waiting: (n: number) => `${n} waiting`,
    status: 'Status',
    anyStatus: 'Any status',
    search: 'Search name, email or company',
    empty: 'No demo requests yet.',
    theirMessage: 'What they asked for',
    website: 'Site',
    language: 'Language',
    source: 'From',
    handled: 'Picked up',
    notes: 'Internal notes',
    saveNote: 'Save note',
    columns: { who: 'Who', company: 'Company', team: 'Team', status: 'Status', received: 'Received' },
    statuses: {
      NEW: 'New',
      CONTACTED: 'Contacted',
      QUALIFIED: 'Qualified',
      WON: 'Won',
      CLOSED: 'Closed',
    },
  },
  recaptcha: {
    title: 'reCAPTCHA',
    subtitle:
      'Google reCAPTCHA on the public forms. It answers a different question than the rate limiter already in front of them: not how fast somebody may try, but whether the thing trying is a person. Create a key pair at',
    enable: 'Protect the public forms',
    version: 'Version',
    v3Hint: 'invisible, scored',
    v2Hint: 'checkbox',
    siteKey: 'Site key',
    siteKeyHint: 'Public: it appears in the page source of every site that uses reCAPTCHA.',
    secretKey: 'Secret key',
    secretKeyHint: 'Stored encrypted and never shown again. Leave blank to keep the current one.',
    secretStored: 'A secret key is stored',
    minScore: 'Minimum score',
    minScoreHint:
      '0 is certainly a bot, 1 is certainly a person. Google suggests 0.5 to start; raise it if spam gets through, lower it if real people are refused.',
    protect: 'Which forms to protect',
    protectHint: 'Each one is somewhere a bot actually turns up.',
    actions: {
      register: 'Sign-up',
      login: 'Sign-in',
      forgotPassword: 'Forgot password',
    },
    actionHints: {
      register: 'Where fake accounts are created.',
      login: 'Credential stuffing. Already rate limited, so a false refusal here costs a real customer their sign-in.',
      forgotPassword: 'Where a mailbox gets flooded with reset links it never asked for.',
    },
    failOpen:
      'If Google cannot be reached, requests are allowed through and a warning is logged. Locking everyone out during someone else’s outage would be worse, and the rate limiter is still in front. An air-gapped deployment should leave this off rather than rely on that.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'reCAPTCHA settings saved',
  },
  email: {
    title: 'Email',
    subtitle:
      'Outbound transactional email (verification links, invitations). Settings are saved to the platform and apply immediately. No redeploy needed. While disabled or unconfigured, emails are logged instead of sent.',
    sendEmail: 'Send email',
    ses: 'Amazon SES',
    fromAddress: 'From address',
    fromPlaceholder: 'ReplayOps <no-reply@yourdomain.com>',
    smtpHost: 'SMTP host',
    port: 'Port',
    portHelp: '465 = SSL, 587 = TLS',
    smtpUser: 'SMTP username',
    smtpPassword: 'SMTP password',
    save: 'Save settings',
    testTitle: 'Send a test email',
    testHint: 'Save your settings first, then send a test to confirm delivery works.',
    recipient: 'Recipient',
  },
  onboarding: {
    title: 'Onboarding',
    subtitle:
      'Set up a new customer in one step: the company, its owner, a project and the ingest key. The owner is emailed a link to choose their own password. Nobody sets one for them.',
    company: 'Company',
    companyPlaceholder: 'Pepsi',
    ownerEmail: 'Owner email',
    fullName: 'Full name (optional)',
    plan: 'Plan',
    planFree: 'Free (default)',
    creating: 'Creating…',
    create: 'Create & invite',
    failed: 'Could not onboard this company.',
    readyEmailed: (email: string) =>
      `is ready. ${email} has been emailed a link to set their password. It expires in 7 days.`,
    readyNoEmail: (email: string) =>
      `is ready, but the invitation email could not be sent (check Admin → Email). Send ${email} the link below yourself. It is the only way into the account.`,
    credentialsFor: (org: string) => `Credentials for ${org}`,
  },
};

const tr: typeof en = {
  needSuperAdmin: 'Bu sayfayı görmek için platform süper yönetici erişimi gerekir.',
  users: {
    title: 'Kullanıcılar',
    subtitle:
      'Tüm kiracılardaki tüm kullanıcılar. Hesapları açıp kapatabilir, platform süper yönetici yetkisi verebilirsiniz. Bir hesabı kapatmak girişi anında engeller.',
    updated: 'Kullanıcı güncellendi',
    updateFailed: 'Güncelleme başarısız',
    columns: {
      user: 'Kullanıcı',
      companies: 'Şirketler',
      lastLogin: 'Son giriş',
      active: 'Etkin',
      superAdmin: 'Süper yönetici',
    },
    verified: 'E-posta doğrulandı',
    notVerified: 'E-posta doğrulanmadı',
    selfDisable: 'Kendi hesabınızı kapatamazsınız',
    disable: 'Hesabı kapat',
    enable: 'Hesabı aç',
    allowlisted:
      'SUPER_ADMIN_EMAILS listesi üzerinden süper yönetici, kurulum yapılandırmasından değiştirin',
    toggleSuperAdmin: 'Platform süper yöneticiliğini aç/kapat',
  },
  plans: {
    title: 'Plan Kataloğu',
    subtitle:
      'Genel plan kataloğunu düzenleyin: limitler, fiyatlar ve özellik kapıları. Değişiklikler tüm kiracılarda yeni oturumlardan itibaren geçerli olur.',
    columns: {
      plan: 'Plan',
      price: '$/ay',
      sessionsPerDay: 'Oturum / gün',
      sessionsPerMonth: 'Oturum / ay',
      projects: 'Proje',
      integrationTools: 'Entegrasyon aracı',
      coverOps: 'CoverOps',
      retention: 'Saklama (gün)',
      seats: 'Kullanıcı',
      enabled: 'Etkin',
      integrations: 'Entegrasyonlar',
      apm: 'APM',
      pii: 'PII maske',
      ingestPii: 'Alımda PII maske',
      sso: 'SSO',
      tester: 'Test modu',
      testers: 'Test kullanıcısı',
    },
    blankInfinite: 'boş = ∞',
    zeroInfinite: '0 = ∞',
    coverOpsModes: { none: 'Sunulmuyor', addon: 'Ek paket', included: 'Dahil' },
  },
  companies: {
    title: 'Şirketler',
    subtitle:
      'Platformdaki tüm kiracılar. Bir şirketin abonelik planını buradan değiştirin. Bir sonraki oturumlarından itibaren anında geçerli olur.',
    columns: {
      company: 'Şirket',
      owner: 'Sahip',
      members: 'Üye',
      logins: 'Giriş',
      sessions: 'Oturum',
      onboarding: 'Başlangıç',
      status: 'Durum',
      subscription: 'Abonelik',
      renews: 'Yenilenme',
      joined: 'Katılım',
    },
    addons: {
      button: 'Ek paket',
      title: (name: string) => `Ek paketler · ${name}`,
      note: 'Her ek paket, paketin üzerine tek bir sınırı artırır. Oturum bir adettir, 1.000’lik bloklarla satılır.',
      sessions: 'Ek oturum / ay',
      projects: 'Ek proje',
      integrations: 'Ek entegrasyon aracı',
      coverOps: 'CoverOps ek paketi',
      summary: (s: string, p: number, i: number, c: boolean) =>
        [s !== '0' && `+${s} oturum`, p > 0 && `+${p} proje`, i > 0 && `+${i} araç`, c && 'CoverOps']
          .filter(Boolean)
          .join(' · '),
      onFree: 'Önce bir paket atayın; Free paketine ek eklenemez.',
      save: 'Ek paketleri kaydet',
      saved: 'Ek paketler kaydedildi',
    },
    actAs: 'Paneli bu şirket olarak aç (süper yönetici)',
    loginsHint: 'Bu şirketin üyelerinin başarılı girişleri',
    memberColumns: { user: 'Kullanıcı', role: 'Rol', status: 'Durum', logins: 'Giriş', lastLogin: 'Son giriş' },
    detailsFailed: 'Bu şirketin başlangıç bilgileri yüklenemedi.',
  },
  demoRequests: {
    title: 'Demo talepleri',
    subtitle:
      'Landing sayfasındaki formdan gelen talepler. Asıl mesele durum sütunu: hâlâ "Yeni" olan her kayıt, kimsenin dönmediği bir kişidir.',
    waiting: (n: number) => `${n} bekliyor`,
    status: 'Durum',
    anyStatus: 'Tüm durumlar',
    search: 'Ad, e-posta ya da şirket ara',
    empty: 'Henüz demo talebi yok.',
    theirMessage: 'Ne istediği',
    website: 'Site',
    language: 'Dil',
    source: 'Nereden',
    handled: 'Devralındı',
    notes: 'Dahili notlar',
    saveNote: 'Notu kaydet',
    columns: { who: 'Kişi', company: 'Şirket', team: 'Ekip', status: 'Durum', received: 'Geliş' },
    statuses: {
      NEW: 'Yeni',
      CONTACTED: 'İletişime geçildi',
      QUALIFIED: 'Uygun',
      WON: 'Kazanıldı',
      CLOSED: 'Kapatıldı',
    },
  },
  recaptcha: {
    title: 'reCAPTCHA',
    subtitle:
      'Herkese açık formlarda Google reCAPTCHA. Önlerindeki hız sınırlayıcıdan farklı bir soruyu yanıtlar: birinin ne kadar hızlı deneyebileceğini değil, deneyen şeyin insan olup olmadığını. Anahtar çiftini şuradan oluşturun:',
    enable: 'Açık formları koru',
    version: 'Sürüm',
    v3Hint: 'görünmez, puanlı',
    v2Hint: 'onay kutusu',
    siteKey: 'Site anahtarı',
    siteKeyHint: 'Herkese açıktır: reCAPTCHA kullanan her sitenin kaynak kodunda görünür.',
    secretKey: 'Gizli anahtar',
    secretKeyHint: 'Şifreli saklanır ve bir daha gösterilmez. Mevcut olanı korumak için boş bırakın.',
    secretStored: 'Kayıtlı bir gizli anahtar var',
    minScore: 'En düşük puan',
    minScoreHint:
      '0 kesinlikle bot, 1 kesinlikle insan. Google başlangıç için 0,5 öneriyor; spam geçiyorsa yükseltin, gerçek kullanıcılar reddediliyorsa düşürün.',
    protect: 'Hangi formlar korunsun',
    protectHint: 'Her biri botların gerçekten uğradığı bir yer.',
    actions: {
      register: 'Kayıt',
      login: 'Giriş',
      forgotPassword: 'Parolamı unuttum',
    },
    actionHints: {
      register: 'Sahte hesapların açıldığı yer.',
      login: 'Kimlik bilgisi denemeleri. Zaten hız sınırlı; buradaki yanlış bir ret gerçek bir müşteriye girişine mal olur.',
      forgotPassword: 'Bir posta kutusunun istemediği sıfırlama bağlantılarıyla dolduğu yer.',
    },
    failOpen:
      'Google’a erişilemezse istekler geçirilir ve bir uyarı kaydedilir. Başkasının kesintisi yüzünden herkesi dışarıda bırakmak daha kötü olurdu, üstelik hız sınırlayıcı hâlâ önde. Ağdan yalıtılmış bir kurulum buna güvenmek yerine bu özelliği kapalı bırakmalı.',
    save: 'Kaydet',
    saving: 'Kaydediliyor…',
    saved: 'reCAPTCHA ayarları kaydedildi',
  },
  email: {
    title: 'E-posta',
    subtitle:
      'Giden işlem e-postaları (doğrulama bağlantıları, davetler). Ayarlar platforma kaydedilir ve anında geçerli olur. Yeniden dağıtım gerekmez. Kapalı ya da yapılandırılmamışken e-postalar gönderilmek yerine günlüğe yazılır.',
    sendEmail: 'E-posta gönder',
    ses: 'Amazon SES',
    fromAddress: 'Gönderen adresi',
    fromPlaceholder: 'ReplayOps <no-reply@alanadiniz.com>',
    smtpHost: 'SMTP sunucusu',
    port: 'Port',
    portHelp: '465 = SSL, 587 = TLS',
    smtpUser: 'SMTP kullanıcı adı',
    smtpPassword: 'SMTP parolası',
    save: 'Ayarları kaydet',
    testTitle: 'Test e-postası gönder',
    testHint: 'Önce ayarlarınızı kaydedin, sonra teslimatı doğrulamak için bir test gönderin.',
    recipient: 'Alıcı',
  },
  onboarding: {
    title: 'Başlangıç',
    subtitle:
      'Yeni bir müşteriyi tek adımda kurun: şirket, sahibi, bir proje ve veri alım anahtarı. Sahibine kendi parolasını seçmesi için bir bağlantı e-postalanır. Kimse onun yerine parola belirlemez.',
    company: 'Şirket',
    companyPlaceholder: 'Pepsi',
    ownerEmail: 'Sahip e-postası',
    fullName: 'Ad soyad (isteğe bağlı)',
    plan: 'Plan',
    planFree: 'Ücretsiz (varsayılan)',
    creating: 'Oluşturuluyor…',
    create: 'Oluştur ve davet et',
    failed: 'Bu şirket oluşturulamadı.',
    readyEmailed: (email: string) =>
      `hazır. ${email} adresine parolasını belirlemesi için bir bağlantı gönderildi, 7 gün geçerli.`,
    readyNoEmail: (email: string) =>
      `hazır, ancak davet e-postası gönderilemedi (Yönetim → E-posta’yı kontrol edin). Aşağıdaki bağlantıyı ${email} adresine kendiniz iletin. Hesaba girmenin tek yolu budur.`,
    credentialsFor: (org: string) => `${org} için kimlik bilgileri`,
  },
};

export const admin = { en, tr };
